import { m } from "@nerves/i18n/messages";
import { type ReactNode, useState } from "react";
import { Dialog as AriaDialog } from "react-aria-components";
import { ApiError } from "@/lib/api";
import { Button } from "./Button";
import { InlineError } from "./InlineError";
import { DialogBody, DialogFooter, DialogHeader, Modal } from "./Modal";

/**
 * Confirmation for destructive or hard-to-undo actions (alert dialog: focus starts on Cancel, so a
 * stray Enter never confirms). `onConfirm` may throw; the message is shown inline and the dialog
 * stays open. Pass `describeError` to map domain errors (e.g. last_owner) to a sentence.
 *
 *   <ConfirmDialog isOpen onOpenChange title="Revoke token?" description="Anything using it stops working."
 *     confirmLabel="Revoke" tone="danger" onConfirm={() => revoke.mutateAsync(id)} />
 */
export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  confirmLabel,
  tone = "danger",
  onConfirm,
  describeError,
  confirmDisabled,
  children,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  tone?: "danger" | "primary";
  onConfirm: () => Promise<unknown> | unknown;
  describeError?: (e: unknown) => string;
  /** Keeps the confirm button disabled (e.g. until the user typed the name of what they are deleting). */
  confirmDisabled?: boolean;
  /** Extra content between the description and the buttons (a checkbox, a consequence list). */
  children?: ReactNode;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(o) => {
        if (!o) setError(undefined);
        onOpenChange(o);
      }}
      size="sm"
    >
      <AriaDialog role="alertdialog" className="flex flex-col outline-none">
        {({ close }) => (
          <>
            <DialogHeader title={title} />
            <DialogBody className="flex flex-col gap-3">
              <p className="text-pretty">{description}</p>
              {children}
              {error ? <InlineError message={error} /> : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" autoFocus onPress={close}>
                {m.common_cancel()}
              </Button>
              <Button
                variant={tone === "danger" ? "danger" : "primary"}
                isPending={pending}
                isDisabled={confirmDisabled}
                onPress={async () => {
                  setPending(true);
                  setError(undefined);
                  try {
                    await onConfirm();
                    close();
                  } catch (e) {
                    setError(
                      describeError?.(e) ??
                        (e instanceof ApiError && e.isNetwork ? m.error_network() : m.error_generic()),
                    );
                  } finally {
                    setPending(false);
                  }
                }}
              >
                {confirmLabel}
              </Button>
            </DialogFooter>
          </>
        )}
      </AriaDialog>
    </Modal>
  );
}
